import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  PRODUCTS,
  EDITION_ORDER,
  EDITION_LABELS,
  EDITION_SHORT,
  EXPRESS_MAX_DEVICES,
  buildCatalog,
  cartFromQuantities,
  summarizeConfiguration,
} from "@/lib/configurator";
import { shareProject } from "@/lib/api";
import { saveProject, newProjectId } from "@/lib/storage";
import { exportConfigPdf } from "@/lib/pdfExport";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Cpu,
  Server,
  ShieldCheck,
  Building2,
  Minus,
  Plus,
  Package,
  Sparkles,
  ExternalLink,
  Save,
  Download,
  FileJson,
  Mail,
  Copy,
  RotateCcw,
  AlertTriangle,
  Info,
  XCircle,
  ShoppingCart,
  Trash2,
  Equal,
} from "lucide-react";

const EDITION_META = {
  express: { Icon: Cpu, subtitle: "Bis 100 Geräte, 1 Standort" },
  professional: { Icon: Server, subtitle: "Unbegrenzt, Multi-Server" },
  expert: { Icon: ShieldCheck, subtitle: "Failover, Verschlüsselung" },
  corporate: { Icon: Building2, subtitle: "Federation, Evidence Lock" },
};

const LEVEL_STYLE = {
  error: { Icon: XCircle, cls: "border-rose-500/40 bg-rose-500/10 text-rose-200" },
  warning: { Icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
  info: { Icon: Info, cls: "border-primary/40 bg-primary/10 text-foreground" },
};

const MAX_QTY = 100000;
const clamp = (n) => Math.max(0, Math.min(MAX_QTY, Number.isFinite(n) ? n : 0));

function QtyInput({ value, onChange, testId, disabled }) {
  const v = Number(value) || 0;
  return (
    <div
      className={cn(
        "inline-flex items-center rounded-md border border-border bg-secondary/40 overflow-hidden",
        v > 0 && "border-primary/60 ring-1 ring-primary/30",
        disabled && "opacity-50"
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-testid={`${testId}-minus`}
        className="h-10 w-10 rounded-none text-muted-foreground hover:text-foreground"
        onClick={() => onChange(clamp(v - 1))}
        disabled={disabled || v <= 0}
        aria-label="Menge verringern"
      >
        <Minus className="w-3.5 h-3.5" />
      </Button>
      <input
        data-testid={testId}
        inputMode="numeric"
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(clamp(parseInt(e.target.value.replace(/\D/g, ""), 10) || 0))}
        onFocus={(e) => e.target.select()}
        className="w-16 h-10 bg-transparent text-center font-semibold tabular-nums outline-none focus-visible:bg-primary/10"
        aria-label="Menge"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-testid={`${testId}-plus`}
        className="h-10 w-10 rounded-none text-muted-foreground hover:text-foreground"
        onClick={() => onChange(clamp(v + 1))}
        disabled={disabled || v >= MAX_QTY}
        aria-label="Menge erhöhen"
      >
        <Plus className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function ArticleMeta({ item }) {
  return (
    <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2">
      <span className="font-mono">Xortec-Nr. {item.xortecNo}</span>
      <span className="hidden sm:inline">·</span>
      <span className="font-mono">{item.manufacturerNo}</span>
      {item.url && (
        <a href={item.url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
          xortec.de <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

export default function ConfigurationMode({ initialProject, onProjectConsumed, onContact }) {
  const [edition, setEdition] = useState("professional");
  const [quantities, setQuantities] = useState({});
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState(newProjectId());
  const [configCode, setConfigCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [origin, setOrigin] = useState(null); // { source, editionLabel }

  // Projekt (aus Code / lokalem Speicher / Assistent) übernehmen
  useEffect(() => {
    if (initialProject && initialProject.mode === "configurator") {
      const cfg = initialProject.configuration || {};
      setQuantities(cfg.quantities || {});
      setEdition(cfg.edition && EDITION_ORDER.includes(cfg.edition) ? cfg.edition : "professional");
      setProjectName(initialProject.name || "");
      setProjectId(initialProject.id || newProjectId());
      setConfigCode(initialProject.code || "");
      setOrigin(initialProject.source ? { source: initialProject.source, editionLabel: initialProject.sourceEditionLabel } : null);
      onProjectConsumed && onProjectConsumed();
    }
  }, [initialProject, onProjectConsumed]);

  const catalog = useMemo(() => buildCatalog(edition), [edition]);
  const licenseItems = catalog.filter((i) => i.group === "license");
  const carePlusItems = catalog.filter((i) => i.group === "carePlus");
  const carePremiumItems = catalog.filter((i) => i.group === "carePremium");

  const cart = useMemo(() => cartFromQuantities(quantities), [quantities]);
  const summary = useMemo(() => summarizeConfiguration(quantities), [quantities]);
  const edStats = summary.perEdition[edition] || { base: 0, devices: 0, carePlus: 0, carePremium: 0, care: 0 };

  const setQty = (key, qty) => {
    setQuantities((prev) => {
      const next = { ...prev };
      if (qty > 0) next[key] = qty;
      else delete next[key];
      return next;
    });
    setConfigCode("");
  };

  const applyDeviceCount = (key) => {
    const devices = Number(quantities[`${edition}:device`]) || 0;
    if (devices <= 0) {
      toast.info("Bitte zuerst die Anzahl der Device-Lizenzen eintragen.");
      return;
    }
    // Nur eine Care-Position je Edition soll die Geräte abdecken – andere Care-Positionen dieser Edition zurücksetzen
    setQuantities((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (k.startsWith(`${edition}:carePlus:`) || k.startsWith(`${edition}:carePremium:`)) delete next[k];
      }
      next[key] = devices;
      return next;
    });
    setConfigCode("");
  };

  const clearEdition = (ed) => {
    setQuantities((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) if (k.startsWith(`${ed}:`)) delete next[k];
      return next;
    });
    setConfigCode("");
  };

  const resetAll = () => {
    setQuantities({});
    setProjectName("");
    setProjectId(newProjectId());
    setConfigCode("");
    setOrigin(null);
    toast.success("Konfiguration zurückgesetzt.");
  };

  const editionCount = (ed) =>
    Object.entries(quantities).reduce((s, [k, v]) => (k.startsWith(`${ed}:`) ? s + (Number(v) || 0) : s), 0);

  const configuration = { mode: "configurator", edition, quantities };

  const handleSave = async () => {
    if (!projectName.trim()) {
      toast.error("Bitte einen Projektnamen eingeben.");
      return;
    }
    if (cart.length === 0) {
      toast.error("Bitte mindestens eine Position auswählen.");
      return;
    }
    setSaving(true);
    let code = configCode;
    try {
      const res = await shareProject({
        name: projectName.trim(),
        mode: "configurator",
        answers: {},
        recommendation: null,
        configuration,
      });
      code = res?.code || code;
      setConfigCode(code);
    } catch (err) {
      toast.error("Konfigurationscode konnte nicht generiert werden.");
    }
    try {
      saveProject({
        id: projectId,
        name: projectName.trim(),
        mode: "configurator",
        answers: {},
        recommendation: null,
        configuration,
        summary: summary.totals,
        code,
      });
      toast.success(`Konfiguration „${projectName.trim()}“ gespeichert.`);
    } catch (e) {
      toast.error("Lokales Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!configCode) return;
    try {
      await navigator.clipboard.writeText(configCode);
      toast.success("Konfigurationscode kopiert.");
    } catch {
      toast.error("Bitte manuell kopieren.");
    }
  };

  const handlePdf = async () => {
    if (cart.length === 0) {
      toast.error("Bitte mindestens eine Position auswählen.");
      return;
    }
    try {
      await exportConfigPdf({ projectName: projectName || "XProtect Lizenz-Konfiguration", cart, summary, configCode });
      toast.success("PDF wird generiert …");
    } catch (e) {
      console.error(e);
      toast.error("PDF-Export fehlgeschlagen.");
    }
  };

  const handleJson = () => {
    const payload = {
      projectName: projectName || null,
      exportedAt: new Date().toISOString(),
      mode: "configurator",
      configuration,
      positions: cart.map((i) => ({
        xortecNo: i.xortecNo,
        manufacturerNo: i.manufacturerNo,
        name: i.name,
        edition: EDITION_LABELS[i.edition],
        category: i.category,
        qty: i.qty,
      })),
      summary,
      configCode: configCode || null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (projectName || "xprotect_konfiguration").replace(/[^a-zA-Z0-9-_ ]/g, "_");
    a.href = url;
    a.download = `${safe}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("JSON exportiert.");
  };

  const handleOffer = () => {
    onContact &&
      onContact({
        projectName,
        mode: "configurator",
        items: cart,
        configCode,
      });
  };

  const renderCareSection = (items, careKey, Icon) => {
    const pkg = PRODUCTS.carePackages[careKey];
    const total = careKey === "carePlus" ? edStats.carePlus : edStats.carePremium;
    return (
      <Card className="p-5 border-border bg-card card-elev" data-testid={`section-${careKey}`}>
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">{pkg.label}</h3>
            {total > 0 && (
              <Badge className="bg-primary/15 text-primary border border-primary/30" data-testid={`badge-${careKey}-total`}>
                {total} Lizenzen
              </Badge>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{pkg.description} Eine Care-Lizenz je Device-Lizenz, Preisstaffel nach Laufzeit.</p>
        <div className="divide-y divide-border/60">
          {items.map((it) => {
            const qty = Number(quantities[it.key]) || 0;
            return (
              <div key={it.key} className={cn("py-3 flex items-center gap-3 flex-wrap", qty > 0 && "bg-primary/5 -mx-2 px-2 rounded-md")}>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-medium text-sm">
                    {it.durationLabel} <span className="text-muted-foreground font-normal">· {it.years}</span>
                  </div>
                  <ArticleMeta item={it} />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid={`btn-apply-devices-${it.key}`}
                  onClick={() => applyDeviceCount(it.key)}
                  className="h-9 border-border text-xs text-muted-foreground hover:text-foreground"
                  title="Menge = Anzahl Device-Lizenzen"
                >
                  <Equal className="w-3.5 h-3.5 mr-1" /> Geräte
                </Button>
                <QtyInput value={qty} onChange={(n) => setQty(it.key, n)} testId={`qty-${it.key}`} />
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  return (
    <div data-testid="configuration-mode" className="space-y-6">
      {/* Intro */}
      <Card className="p-5 md:p-6 border-border bg-card card-elev">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Konfigurationsmodus</div>
            <h2 className="text-xl md:text-2xl font-semibold">Lizenzen direkt zusammenstellen</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Sie wissen bereits, was Sie brauchen? Wählen Sie Edition, Basis- und Device-Lizenzen sowie Care-Pakete mit
              Laufzeit und Menge. Xortec-Artikelnummern werden live ergänzt – speichern per Konfigurationscode oder als PDF exportieren.
            </p>
          </div>
        </div>
      </Card>

      {origin && (
        <div
          data-testid="config-origin-banner"
          className="flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm"
        >
          <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="font-medium">
              {origin.source === "expert" ? "Aus dem Expertenmodus übernommen" : "Aus der Assistenten-Empfehlung übernommen"}
            </span>
            {origin.editionLabel && <span className="text-muted-foreground"> – {origin.editionLabel}</span>}
            <span className="text-muted-foreground">. Mengen, Laufzeiten und Care-Stufen können Sie hier frei anpassen.</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => setOrigin(null)}
            data-testid="btn-dismiss-origin"
          >
            Ausblenden
          </Button>
        </div>
      )}

      {/* Edition selector */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">1. Edition wählen</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="edition-selector">
          {EDITION_ORDER.map((ed) => {
            const { Icon, subtitle } = EDITION_META[ed];
            const active = edition === ed;
            const cnt = editionCount(ed);
            return (
              <button
                key={ed}
                type="button"
                data-testid={`edition-tile-${ed}`}
                onClick={() => setEdition(ed)}
                className={cn(
                  "text-left rounded-xl border p-4 tile-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  active ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-card/70 hover:bg-secondary/60"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", active ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary")}>
                    <Icon className="w-[18px] h-[18px]" />
                  </div>
                  {cnt > 0 && (
                    <Badge className="bg-primary text-primary-foreground" data-testid={`edition-badge-${ed}`}>
                      {cnt}
                    </Badge>
                  )}
                </div>
                <div className="font-semibold text-sm">{EDITION_LABELS[ed]}</div>
                <div className="text-[11px] text-muted-foreground">{subtitle}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start">
        {/* Left: Catalog */}
        <div className="space-y-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
            2. Lizenzen &amp; Care für {EDITION_LABELS[edition]}
          </div>

          {/* Licenses */}
          <Card className="p-5 border-border bg-card card-elev" data-testid="section-licenses">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Lizenzen</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Jedes System benötigt eine Basis-Lizenz plus eine Device-Lizenz je Kamera/Kanal.
              {edition === "express" && ` Express+ unterstützt bis zu ${EXPRESS_MAX_DEVICES} Geräte je System.`}
            </p>
            <div className="divide-y divide-border/60">
              {licenseItems.map((it) => {
                const qty = Number(quantities[it.key]) || 0;
                return (
                  <div key={it.key} className={cn("py-3 flex items-center gap-3 flex-wrap", qty > 0 && "bg-primary/5 -mx-2 px-2 rounded-md")}>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-medium text-sm">{it.name}</div>
                      <div className="text-xs text-muted-foreground">{it.description}</div>
                      <ArticleMeta item={it} />
                    </div>
                    {it.group === "license" && it.key.endsWith(":base") && qty === 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-9 border-primary/40 text-xs"
                        data-testid={`btn-add-base-${edition}`}
                        onClick={() => setQty(it.key, 1)}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> 1× hinzufügen
                      </Button>
                    )}
                    <QtyInput value={qty} onChange={(n) => setQty(it.key, n)} testId={`qty-${it.key}`} />
                  </div>
                );
              })}
            </div>
          </Card>

          {renderCareSection(carePlusItems, "carePlus", ShieldCheck)}
          {renderCareSection(carePremiumItems, "carePremium", Sparkles)}
        </div>

        {/* Right: Summary */}
        <div className="lg:sticky lg:top-24 space-y-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">3. Zusammenfassung</div>
          <Card className="border-border bg-card card-elev overflow-hidden" data-testid="config-summary">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-primary" />
                <h3 className="font-semibold">Ihre Konfiguration</h3>
              </div>
              <Badge variant="outline" className="border-border text-muted-foreground" data-testid="summary-positions">
                {summary.totals.positions} Positionen
              </Badge>
            </div>

            <div className="grid grid-cols-3 divide-x divide-border border-b border-border text-center">
              <div className="py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Editionen</div>
                <div className="text-lg font-bold tabular-nums" data-testid="summary-editions">{summary.totals.editions.length}</div>
              </div>
              <div className="py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Device-Lizenzen</div>
                <div className="text-lg font-bold tabular-nums text-primary" data-testid="summary-devices">{summary.totals.devices}</div>
              </div>
              <div className="py-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Care-Lizenzen</div>
                <div className="text-lg font-bold tabular-nums" data-testid="summary-care">{summary.totals.care}</div>
              </div>
            </div>

            {cart.length === 0 ? (
              <div className="px-5 py-10 text-center" data-testid="summary-empty">
                <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Noch keine Positionen gewählt.</p>
                <p className="text-xs text-muted-foreground mt-1">Fügen Sie links Lizenzen und Care-Pakete hinzu.</p>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto">
                {summary.totals.editions.map((ed) => {
                  const items = cart.filter((i) => i.edition === ed);
                  return (
                    <div key={ed} className="border-b border-border/60 last:border-none">
                      <div className="px-5 py-2 bg-secondary/40 flex items-center justify-between">
                        <span className="text-xs font-semibold text-primary">{EDITION_LABELS[ed]}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-rose-400"
                          onClick={() => clearEdition(ed)}
                          data-testid={`btn-clear-${ed}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Entfernen
                        </Button>
                      </div>
                      <div className="divide-y divide-border/40">
                        {items.map((it) => (
                          <div key={it.key} className="px-5 py-2.5 flex items-start gap-3" data-testid={`summary-item-${it.key}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{it.name}</div>
                              <div className="text-[11px] text-muted-foreground font-mono truncate">
                                {it.xortecNo} · {it.manufacturerNo}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-bold tabular-nums">{it.qty}×</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {summary.warnings.length > 0 && (
              <div className="px-5 py-4 border-t border-border space-y-2" data-testid="config-warnings">
                {summary.warnings.map((w, i) => {
                  const { Icon, cls } = LEVEL_STYLE[w.level] || LEVEL_STYLE.info;
                  return (
                    <div key={i} className={cn("flex gap-2 rounded-md border px-3 py-2 text-xs leading-relaxed", cls)} data-testid={`warning-${w.level}-${i}`}>
                      <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{w.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <Separator className="bg-border" />

            <div className="p-5 space-y-3">
              <Input
                data-testid="config-project-name"
                placeholder="Projektname (zum Speichern)"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="bg-secondary/40 border-border"
              />
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="btn-config-save" onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground col-span-2">
                  <Save className="w-4 h-4 mr-1" /> Speichern &amp; Code erzeugen
                </Button>
                <Button data-testid="btn-config-pdf" variant="outline" onClick={handlePdf}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button data-testid="btn-config-json" variant="outline" onClick={handleJson}>
                  <FileJson className="w-4 h-4 mr-1" /> JSON
                </Button>
                <Button
                  data-testid="btn-config-offer"
                  variant="outline"
                  className="border-primary/40 hover:bg-primary/10 col-span-2"
                  onClick={handleOffer}
                >
                  <Mail className="w-4 h-4 mr-1" /> Angebot anfragen
                </Button>
                <Button data-testid="btn-config-reset" variant="ghost" onClick={resetAll} className="col-span-2 text-muted-foreground hover:text-foreground">
                  <RotateCcw className="w-4 h-4 mr-1" /> Zurücksetzen
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Preise auf Anfrage bei der Xortec GmbH. Alle Artikel werden digital ausgeliefert.</p>
            </div>
          </Card>

          {configCode && (
            <Card data-testid="config-code-card" className="p-5 border-primary/40 bg-primary/5">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Konfigurationscode</div>
              <p className="text-xs text-muted-foreground mb-3">
                Mit diesem 6-stelligen Code öffnen Sie die Konfiguration später wieder – auch in einem anderen Browser (Tab „Projekte“).
              </p>
              <div className="inline-flex items-center gap-2 bg-background/80 border border-primary/40 rounded-lg px-4 py-3">
                <span className="font-mono text-3xl font-bold tracking-[0.35em] text-primary select-all" data-testid="config-code-value">
                  {configCode}
                </span>
                <Button size="sm" variant="ghost" onClick={handleCopyCode} data-testid="btn-config-copy-code" className="ml-1">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
